import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Switch,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { updateClass } from "../../api/backend";
import { useTenant } from "../../contexts/TenantContext";
import HowToGuideBox from "../../components/HowToGuideBox";
import { useConfigList } from "../../hooks/useConfigList";
import { useTheme } from "@mui/material/styles";

const DS = {
  primaryContainer: "var(--color-primary)",
  surface: "var(--color-surface-canvas-light)",
  surfaceContainerLow: "var(--color-surface-press-light)",
  surfaceContainerHigh: "var(--color-surface-press-stronger)",
  onSurface: "var(--color-ink)",
  onSurfaceVariant: "rgba(31,22,51,0.7)",
  outlineVariant: "var(--color-hairline-cloud)",
};

const DEFAULT_CATEGORIES = [
  "Firefighter",
  "EMS",
  "Police",
  "Rescue",
  "Lifeguard",
  "Security",
  "ADMIN",
  "AHA",
  "TACTICAL",
  "Other",
];

function FieldLabel({ children, required }) {
  return (
    <Typography
      sx={{
        fontSize: "0.62rem",
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: DS.onSurfaceVariant,
        mb: 0.75,
      }}
    >
      {children}
      {required && (
        <Box component="span" sx={{ color: "var(--color-danger)", ml: 0.25 }}>
          *
        </Box>
      )}
    </Typography>
  );
}

export default function DetailsTab({ course, canEdit }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryContainer = theme.palette.primary.dark;
  const CATEGORIES = useConfigList("COURSE_CATEGORY", DEFAULT_CATEGORIES);

  const filledSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      bgcolor: DS.surfaceContainerLow,
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": { border: `2px solid ${tPrimary}` },
      "&.Mui-focused": { bgcolor: "var(--color-surface-canvas-light)" },
    },
    "& .MuiInputBase-input": {
      fontSize: "0.9rem",
      color: DS.onSurface,
      py: 1.5,
      px: 1.75,
    },
    "& .MuiInputBase-input::placeholder": {
      color: DS.onSurfaceVariant,
      opacity: 0.7,
    },
  };
  const { id } = useParams();
  const { user, isFeatureEnabled } = useTenant();
  const showPublicListing = isFeatureEnabled
    ? isFeatureEnabled("publicClassListing")
    : false;

  const [form, setForm] = useState({
    title: "",
    courseNumber: "",
    category: "",
    availableSeats: 20,
    waitlistEnabled: false,
    description: "",
    prereqs: "",
    useGenericTerms: true,
    termsConditions: "",
    feeMode: "FREE_ALL",
    feeAmount: 0,
    visibility: "TENANT_ONLY",
  });
  const [loading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("success");

  // Initialize form from course data
  useEffect(() => {
    if (course) {
      setForm({
        title: course.title || "",
        courseNumber: course.courseNumber || "",
        category: course.category || "",
        availableSeats: course.availableSeats || 20,
        waitlistEnabled: course.waitlistEnabled === true,
        description: course.description || "",
        prereqs:
          course.prereqs && course.prereqs !== "None" ? course.prereqs : "",
        useGenericTerms: course.useGenericTerms !== false,
        termsConditions: course.termsConditions || "",
        feeMode: course.feeMode || "FREE_ALL",
        feeAmount: course.feeAmount || 0,
        visibility: course.visibility || "TENANT_ONLY",
      });
    }
  }, [course]);

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setMessage(null);
  };

  const handleSave = async () => {
    if (!form.title) {
      setMessageType("error");
      setMessage("Course title is required.");
      return;
    }

    setSaving(true);
    try {
      await updateClass(user?.tenantId, id, {
        ...form,
        prereqs: form.prereqs || "None",
        feeAmount: parseFloat(form.feeAmount) || 0,
      });
      setMessageType("success");
      setMessage("Course details saved successfully!");
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessageType("error");
      setMessage(err.message || "Failed to save course details.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} color={tPrimary} sx={{ fontFamily: "var(--font-display)" }}>
          Course Information
        </Typography>
        <HowToGuideBox
          title="How to Edit Course Details"
          role="Course Coordinators"
          steps={[
            {
              number: 1,
              title: "Enter Course Title",
              description:
                "This is the main name of your course that students will see.",
              details:
                "Use a clear, descriptive title that indicates the course subject and level (e.g., 'Advanced Fire Suppression Techniques')",
            },
            {
              number: 2,
              title: "Add a Course Number (Optional)",
              description:
                "Assign a unique identifier for tracking and organization.",
              details:
                "Follow your organization's naming convention (e.g., FS-2024-001)",
            },
            {
              number: 3,
              title: "Select a Category",
              description:
                "Choose the training domain (Firefighter, EMS, Police, etc.).",
              details:
                "Categories help organize and filter courses across your training center.",
            },
            {
              number: 4,
              title: "Set Available Seats",
              description:
                "Define the maximum number of students who can enroll.",
              details: "This helps manage class size and resource allocation.",
            },
            {
              number: 5,
              title: "Write Course Description",
              description:
                "Outline the learning objectives, topics covered, and what students will gain.",
              details:
                "Be specific about skills, certifications, and practical outcomes.",
            },
            {
              number: 6,
              title: "List Prerequisites (Optional)",
              description:
                "Specify any courses or certifications students must complete first.",
              details:
                "This helps ensure students are prepared for your course.",
            },
            {
              number: 7,
              title: "Save Your Changes",
              description: "Click Save to store all course information.",
              details:
                "Changes are immediately reflected in the course listing.",
            },
          ]}
          tips={[
            "Make your course title descriptive and searchable. Students use this to find courses.",
            "Prerequisites help manage course progression and student readiness.",
            "Save frequently to avoid losing changes.",
          ]}
        />
      </Box>

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Only Course Coordinators can edit course details.
        </Alert>
      )}

      {message && (
        <Alert
          severity={messageType}
          onClose={() => setMessage(null)}
          sx={{ mb: 3 }}
        >
          {message}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${DS.outlineVariant}`,
          borderRadius: "12px",
          bgcolor: "var(--color-surface-canvas-light)",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Course Title */}
          <Box>
            <FieldLabel required>Course Title</FieldLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Advanced Digital Security Protocol"
              value={form.title}
              onChange={set("title")}
              disabled={!canEdit}
              sx={filledSx}
            />
          </Box>

          {/* Course Number + Category */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <FieldLabel>Course Number</FieldLabel>
              <TextField
                fullWidth
                size="small"
                placeholder="LMN-2024-01"
                value={form.courseNumber}
                onChange={set("courseNumber")}
                disabled={!canEdit}
                sx={filledSx}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FieldLabel>Category</FieldLabel>
              <Select
                fullWidth
                size="small"
                value={form.category || ""}
                onChange={set("category")}
                displayEmpty
                disabled={!canEdit}
                renderValue={(v) =>
                  v || (
                    <span style={{ color: DS.onSurfaceVariant, opacity: 0.7 }}>
                      Select a category
                    </span>
                  )
                }
                sx={{
                  borderRadius: "8px",
                  bgcolor: DS.surfaceContainerLow,
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: `2px solid ${tPrimary}`,
                  },
                  "&.Mui-focused": { bgcolor: "var(--color-surface-canvas-light)" },
                  fontSize: "0.9rem",
                }}
              >
                <MenuItem value="">
                  <em style={{ color: DS.onSurfaceVariant }}>
                    Select a category
                  </em>
                </MenuItem>
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Stack>

          {/* Available Seats */}
          <Box>
            <FieldLabel>Available Seats</FieldLabel>
            <TextField
              fullWidth
              size="small"
              type="number"
              value={form.availableSeats}
              onChange={set("availableSeats")}
              disabled={!canEdit}
              sx={filledSx}
            />
          </Box>

          {/* Waitlist */}
          <Box>
            <FieldLabel>Waitlist</FieldLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={form.waitlistEnabled === true}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      waitlistEnabled: e.target.checked,
                    }));
                    setMessage(null);
                  }}
                  disabled={!canEdit}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Enable waitlist — students can join a waitlist when the class
                  is full
                </Typography>
              }
            />
          </Box>

          {/* Description */}
          <Box>
            <FieldLabel>Description</FieldLabel>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Outline the learning objectives and course overview..."
              value={form.description || ""}
              onChange={set("description")}
              disabled={!canEdit}
              sx={{
                ...filledSx,
                "& .MuiInputBase-textarea": {
                  fontSize: "0.9rem",
                  color: DS.onSurface,
                  py: 1.25,
                  px: 1.75,
                },
              }}
            />
          </Box>

          {/* Prerequisites */}
          <Box>
            <FieldLabel>Prerequisites</FieldLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Intro to Systems, Certification Level 1"
              value={form.prereqs || ""}
              onChange={set("prereqs")}
              disabled={!canEdit}
              sx={filledSx}
            />
          </Box>

          {/* Terms & Conditions Section */}
          <Box sx={{ pt: 2 }}>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: tPrimary,
                mb: 1.5,
              }}
            >
              Terms &amp; Conditions
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={form.useGenericTerms !== false}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      useGenericTerms: e.target.checked,
                    }));
                    setMessage(null);
                  }}
                  disabled={!canEdit}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Use generic T&amp;Cs from Training Center settings
                </Typography>
              }
            />
            {form.useGenericTerms === false && (
              <Box sx={{ mt: 1.5 }}>
                <FieldLabel>Class-Specific Terms &amp; Conditions</FieldLabel>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Enter the terms and conditions that students must accept before registering for this class..."
                  value={form.termsConditions || ""}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      termsConditions: e.target.value,
                    }));
                    setMessage(null);
                  }}
                  disabled={!canEdit}
                  sx={{
                    ...filledSx,
                    "& .MuiInputBase-textarea": {
                      fontSize: "0.875rem",
                      py: 1.25,
                      px: 1.75,
                    },
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Pricing Section */}
          <Box sx={{ pt: 2 }}>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: tPrimary,
                mb: 1.5,
              }}
            >
              Pricing
            </Typography>

            {/* Class Price */}
            <Box sx={{ mb: 2 }}>
              <FieldLabel>Class Price (USD)</FieldLabel>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={form.feeAmount ?? 0}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    feeAmount: parseFloat(e.target.value) || 0,
                  }));
                  setMessage(null);
                }}
                disabled={!canEdit}
                sx={{ ...filledSx, maxWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Fee Mode */}
            <Box>
              <FieldLabel>Fee Mode</FieldLabel>
              <Select
                fullWidth
                size="small"
                value={form.feeMode || "FREE_ALL"}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    feeMode: e.target.value,
                  }));
                  setMessage(null);
                }}
                disabled={!canEdit}
                sx={{
                  borderRadius: "8px",
                  bgcolor: DS.surfaceContainerLow,
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: `2px solid ${tPrimary}`,
                  },
                  "&.Mui-focused": { bgcolor: "var(--color-surface-canvas-light)" },
                  fontSize: "0.9rem",
                }}
              >
                <MenuItem value="FREE_ALL">Free for everyone</MenuItem>
                <MenuItem value="PAID_ALL">
                  Paid for everyone (use class price above)
                </MenuItem>
                <MenuItem value="FREE_IN_AGENCY_PAID_OUT">
                  Free for agency members / Paid for others
                </MenuItem>
              </Select>
              <Typography
                variant="caption"
                sx={{
                  color: DS.onSurfaceVariant,
                  mt: 0.75,
                  display: "block",
                }}
              >
                {form.feeMode === "FREE_IN_AGENCY_PAID_OUT"
                  ? `Students with an approved agency membership register free. Others are charged $${Number(form.feeAmount || 0).toFixed(2)}.`
                  : form.feeMode === "PAID_ALL"
                    ? `All students are charged $${Number(form.feeAmount || 0).toFixed(2)} to register.`
                    : "All students register for free."}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Publishing visibility (only shown when publicClassListing feature is enabled) */}
        {showPublicListing && (
          <>
            <Divider sx={{ my: 3, borderColor: DS.outlineVariant }} />
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 800,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: tPrimary,
                  mb: 2,
                }}
              >
                Publishing
              </Typography>
              <Box>
                <FieldLabel>Course Visibility</FieldLabel>
                <Select
                  fullWidth
                  value={form.visibility || "TENANT_ONLY"}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, visibility: e.target.value }))
                  }
                  disabled={!canEdit}
                  sx={{
                    borderRadius: "8px",
                    bgcolor: DS.surfaceContainerLow,
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                    "&.Mui-focused fieldset": {
                      border: `2px solid ${tPrimary}`,
                    },
                    fontSize: "0.9rem",
                    color: DS.onSurface,
                  }}
                >
                  <MenuItem value="TENANT_ONLY">
                    Tenant Only — visible only to members of this training
                    center
                  </MenuItem>
                  <MenuItem value="PUBLIC_ONLY">
                    Public Only — visible on the public course listing page
                  </MenuItem>
                  <MenuItem value="BOTH">
                    Both — visible to members and on the public listing
                  </MenuItem>
                </Select>
                <Typography
                  variant="caption"
                  sx={{
                    color: DS.onSurfaceVariant,
                    mt: 0.75,
                    display: "block",
                  }}
                >
                  {form.visibility === "PUBLIC_ONLY"
                    ? "This course will appear on the public listing page but not in the member class list."
                    : form.visibility === "BOTH"
                      ? "This course will appear both in the member class list and on the public listing page."
                      : "This course is only visible to authenticated members of this training center."}
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* Divider */}
        <Divider sx={{ my: 3, borderColor: DS.outlineVariant }} />

        {/* Action buttons */}
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<SaveIcon fontSize="small" />}
            onClick={handleSave}
            disabled={saving || !form.title || !canEdit}
            sx={{
              bgcolor: tPrimary,
              color: "var(--color-on-primary)",
              fontWeight: 700,
              textTransform: "none",
              fontSize: "0.9rem",
              px: 3,
              borderRadius: "8px",
              boxShadow: "none",
              "&:hover": {
                bgcolor: tPrimaryContainer,
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                bgcolor: DS.surfaceContainerHigh,
                color: DS.onSurfaceVariant,
              },
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
